import React from 'react';
import WiedzaArticleLayout from '../components/WiedzaArticleLayout';

/**
 * Article 3 — "In Europe, AI Must Explain Itself"
 * PL:  W Europie AI musi być przejrzyste
 * Route (PL primary): /wiedza/w-europie-ai-musi-byc-przejrzyste
 * Route (EN alias):   /knowledge/in-europe-ai-must-explain-itself
 * Published: 2026-08-17
 */
const WiedzaWEuropieAiPrzejrzyste = () => (
  <WiedzaArticleLayout
    slugId="in-europe-ai-must-explain-itself"
    canonicalUrl="https://hussarai.org/wiedza/w-europie-ai-musi-byc-przejrzyste"
    publishedDate="2026-08-17"
    meta={{
      titlePl: 'W Europie AI musi być przejrzyste',
      titleEn: 'In Europe, AI Must Explain Itself',
      descriptionPl:
        'Europejskie ramy AI wymagają przejrzystości, śladu i wyraźnego ' +
        'oddzielenia informacji generowanych przez maszynę od decyzji ' +
        'człowieka. Jak wpisuje się w to architektura HussarAi.',
      descriptionEn:
        'European AI governance is moving toward transparency, ' +
        'traceability, and a visible separation between machine output ' +
        'and human judgment. How HussarAi’s architecture fits in.',
    }}
    content={{
      eyebrowPl: 'Wiedza · Europejskie ramy AI',
      eyebrowEn: 'Knowledge · European AI framework',
      ledePl:
        'Wiek „ufaj maszynie” dobiega w Europie końca. Coraz bardziej ' +
        'oczekuje się od AI: pokaż fakt źródłowy, pokaż obserwację, ' +
        'pokaż człowieka, który to zweryfikował.',
      ledeEn:
        'The "trust the machine" era is ending in Europe. The expectation ' +
        'is increasingly clear: show the source fact, show the observation, ' +
        'show the human who reviewed it.',
      bodyPl: [
        { type: 'h2', text: 'Nie chodzi tylko o zgodność z przepisami' },
        { type: 'p', text:
          'Europejskie regulacje AI (w tym ramy Aktu o AI) nie są tylko technicznym obowiązkiem. Wyrażają starszą, głęboko europejską zasadę: instytucje muszą tłumaczyć swoje działania, a osoby, których one dotyczą, mają prawo je sprawdzić. Kiedy maszyna wchodzi między instytucję a człowieka, musi umieć wytłumaczyć się w podobny sposób.' },
        { type: 'h2', text: 'Trzy widoczne warstwy w HussarAi' },
        { type: 'callout', text: 'FAKT ŹRÓDŁOWY · OBSERWACJA AI · WERYFIKACJA CZŁOWIEKA' },
        { type: 'p', text:
          'Ten łańcuch nie jest metaforą — jest wpisany w kod. Fakt źródłowy to atomowa jednostka pochodząca z konkretnego dokumentu. Obserwacja AI to sygnał zbudowany nad faktem, nigdy nad domysłem. Weryfikacja człowieka to rejestrowany, HMAC-podpisany krok, którego nie da się cofnąć bez śladu.' },
        { type: 'h2', text: 'Wyraźne oddzielenie AI od decyzji' },
        { type: 'p', text:
          'Europejskie oczekiwanie „AI musi się tłumaczyć” najlepiej realizuje architektura, która pokazuje ludziom, gdzie kończy się AI, a zaczyna człowiek. Dlatego HussarAi twardo zatrzymuje się przed warstwą decyzyjną. Decyduje właściwy urząd, nie my.' },
        { type: 'h2', text: 'MOS ≠ HussarAi' },
        { type: 'p', text:
          'MOS jest oficjalnym systemem składania wniosków w Polsce — to część administracji publicznej. HussarAi jest niezależnym narzędziem przygotowania i sprawdzenia spójności akt. Nie składamy wniosków w imieniu klienta, nie działamy jako pośrednik MOS, nie sugerujemy integracji z urzędem. Jesteśmy warstwą przygotowawczą — nic więcej, nic mniej.' },
        { type: 'h2', text: 'Dlaczego to praktyczne' },
        { type: 'p', text:
          'Osobie składającej wniosek pomaga to spać spokojniej: wie, że każda uwaga systemu jest oparta o konkretny dokument. Instytucji, która potem sprawdza akta, ułatwia to pracę: ma ślad, komu podziękować, komu zadać pytanie i gdzie zajrzeć. A regulator widzi, że AI nie schowała się w czarnej skrzynce.' },
      ],
      bodyEn: [
        { type: 'h2', text: 'This is not only about regulatory compliance' },
        { type: 'p', text:
          'European AI regulations (including the AI Act framework) are not merely a technical obligation. They express an older, deeply European principle: institutions must explain themselves, and the people they affect have the right to check the explanation. When a machine steps in between an institution and a person, it must be able to explain itself in a similar way.' },
        { type: 'h2', text: 'Three visible layers in HussarAi' },
        { type: 'callout', text: 'SOURCE FACT · AI OBSERVATION · HUMAN REVIEW' },
        { type: 'p', text:
          'This chain is not a metaphor — it is coded into the product. A source fact is an atomic unit coming from a concrete document. An AI observation is a signal built on top of the fact, never on top of a guess. Human review is a recorded, HMAC-signed step that cannot be undone without a trace.' },
        { type: 'h2', text: 'Visible separation between AI and decision' },
        { type: 'p', text:
          'The European expectation that "AI must explain itself" is best served by an architecture that shows people where AI ends and where a human begins. That is why HussarAi stops firmly before the decision layer. The competent authority decides, not us.' },
        { type: 'h2', text: 'MOS ≠ HussarAi' },
        { type: 'p', text:
          'MOS is the official Polish government submission system — a part of public administration. HussarAi is an independent preparation and record-integrity tool. We do not submit applications on anyone’s behalf, we do not act as a MOS intermediary, and we do not suggest integration with the authority. We are a preparation layer — nothing more, nothing less.' },
        { type: 'h2', text: 'Why this is practical' },
        { type: 'p', text:
          'For the applicant, it means peace of mind: every comment the system makes is grounded in a specific document. For the institution reviewing the record later, it makes work easier: there is a trace of who to thank, who to ask, and where to look. For the regulator, it shows that AI has not hidden inside a black box.' },
      ],
    }}
    internalLinks={[
      { to: '/wiedza', labelPl: 'Wiedza', labelEn: 'Knowledge' },
      { to: '/mos/precheck', labelPl: 'Weryfikacja przed MOS', labelEn: 'MOS PreCheck' },
      { to: '/ai-act-polska', labelPl: 'AI Act w Polsce', labelEn: 'AI Act in Poland' },
      { to: '/bezpieczenstwo-i-zgodnosc', labelPl: 'Bezpieczeństwo i zgodność', labelEn: 'Security and governance' },
    ]}
  />
);

export default WiedzaWEuropieAiPrzejrzyste;
